import { memo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import SolidBedIcon from 'LuxKit/Icons/solid/SolidBedIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import SolidCameraPlusIcon from 'LuxKit/Icons/solid/SolidCameraPlusIcon'
import SolidTicketIcon from 'LuxKit/Icons/solid/SolidTicketIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import { chatExploreCategories } from 'tripPlanner/pages/chat/chatExploreCategories'
import {
  MARKER_TYPE_STOP,
  MARKER_TYPE_CHAT_HOTEL,
  MARKER_TYPE_CHAT_EXPERIENCE,
  MARKER_TYPE_EXPLORE_EXPERIENCE,
  MARKER_TYPE_ITEM,
  MARKER_TYPE_EXPLORE,
  MARKER_TYPE_EXPLORE_HOTEL,
  MARKER_TYPE_SUGGESTED,
} from 'tripPlanner/utils/mapMarkerTransforms'
import type { MapMarkerComponent } from 'tripPlanner/utils/mapMarkerTransforms'

const MARKER_PIN_ACCENT = '#D43044'

const MarkerPin = styled(Group)`
  width: ${rem(28)};
  height: ${rem(28)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  flex-shrink: 0;
  box-shadow: 0 ${rem(2)} ${rem(6)} rgba(0, 0, 0, 0.25);

  &.${MARKER_TYPE_EXPLORE},
  &.${MARKER_TYPE_EXPLORE_HOTEL},
  &.${MARKER_TYPE_EXPLORE_EXPERIENCE},
  &.${MARKER_TYPE_SUGGESTED} {
    background-color: ${MARKER_PIN_ACCENT};
  }

  &.${MARKER_TYPE_CHAT_HOTEL} {
    background-color: ${props => props.theme.palette.neutral.default.one};
    border: ${rem(2)} solid ${props => props.theme.palette.neutral.default.eight};
  }

  &.${MARKER_TYPE_CHAT_EXPERIENCE} {
    background-color: ${props => props.theme.palette.neutral.default.one};
    border: ${rem(2)} solid ${props => props.theme.palette.neutral.default.eight};
  }
`

const InlineMarkerWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  transition: transform 0.15s ease-out;

  &.highlighted {
    transform: scale(1.15);
    z-index: 10;
  }
`

const InlineMarkerLabel = styled.span`
  position: absolute;
  left: 100%;
  margin-left: ${rem(4)};
  padding: ${rem(4)} ${rem(8)};
  background: rgba(255, 255, 255, 0.95);
  border-radius: ${props => props.theme.borderRadius.M};
  font-size: ${rem(12)};
  font-weight: ${props => props.theme.font.primary.weight.bold};
  color: ${props => props.theme.palette.neutral.default.one};
  box-shadow: 0 ${rem(1)} ${rem(4)} rgba(0, 0, 0, 0.15);
  white-space: nowrap;
  max-width: ${rem(150)};
  overflow: hidden;
  text-overflow: ellipsis;
`

const StopMarkerWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const StopMarkerLabel = styled(InlineMarkerLabel)`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: ${rem(4)};
  max-width: ${rem(120)};
`

const exploreIconByLabel: Record<string, typeof chatExploreCategories[number]['icon']> = {}
chatExploreCategories.forEach(category => {
  exploreIconByLabel[category.label] = category.icon
})

interface Props {
  marker: MapMarkerComponent
  deemphasizeBaseMarkers: boolean
  isHighlighted: boolean
}

function ChatMapMarkerContent({
  marker,
  deemphasizeBaseMarkers,
  isHighlighted,
}: Props) {
  if (marker.markerType === MARKER_TYPE_STOP) {
    return (
      <StopMarkerWrapper>
        <svg
          width="27"
          height="41"
          viewBox="0 0 27 41"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M13.5 0C6.044 0 0 6.044 0 13.5C0 23.625 13.5 41 13.5 41S27 23.625 27 13.5C27 6.044 20.956 0 13.5 0Z"
            fill="#EA4335"
          />
          <circle cx="13.5" cy="13.5" r="5.5" fill="#ffffff" />
        </svg>
        {!deemphasizeBaseMarkers && marker.label && <StopMarkerLabel>{marker.label}</StopMarkerLabel>}
      </StopMarkerWrapper>
    )
  }

  if (marker.markerType === MARKER_TYPE_CHAT_HOTEL) {
    return (
      <InlineMarkerWrapper className={isHighlighted ? 'highlighted' : undefined}>
        <MarkerPin direction="horizontal" verticalAlign="center" horizontalAlign="center" className={MARKER_TYPE_CHAT_HOTEL}>
          <SolidBedIcon size="XS" colour="neutral-eight" />
        </MarkerPin>
        {marker.label && <InlineMarkerLabel>{marker.label}</InlineMarkerLabel>}
      </InlineMarkerWrapper>
    )
  }

  if (marker.markerType === MARKER_TYPE_CHAT_EXPERIENCE) {
    return (
      <InlineMarkerWrapper className={isHighlighted ? 'highlighted' : undefined}>
        <MarkerPin direction="horizontal" verticalAlign="center" horizontalAlign="center" className={MARKER_TYPE_CHAT_EXPERIENCE}>
          <SolidTicketIcon size="XS" colour="neutral-eight" />
        </MarkerPin>
        {marker.label && <InlineMarkerLabel>{marker.label}</InlineMarkerLabel>}
      </InlineMarkerWrapper>
    )
  }

  if (marker.markerType === MARKER_TYPE_ITEM) {
    return (
      <InlineMarkerWrapper>
        <MarkerPin direction="horizontal" verticalAlign="center" horizontalAlign="center">
          <SolidCameraPlusIcon size="XS" colour="neutral-two" />
        </MarkerPin>
        {!deemphasizeBaseMarkers && marker.label && <InlineMarkerLabel>{marker.label}</InlineMarkerLabel>}
      </InlineMarkerWrapper>
    )
  }

  if (marker.markerType === MARKER_TYPE_EXPLORE) {
    const ExploreIcon = marker.categoryLabel ? exploreIconByLabel[marker.categoryLabel] : undefined
    return (
      <InlineMarkerWrapper>
        <MarkerPin direction="horizontal" verticalAlign="center" horizontalAlign="center" className={MARKER_TYPE_EXPLORE}>
          {ExploreIcon ? <ExploreIcon size="XS" colour="neutral-eight" /> : <LineMapMarkerIcon size="XS" colour="neutral-eight" />}
        </MarkerPin>
        {marker.label && <InlineMarkerLabel>{marker.label}</InlineMarkerLabel>}
      </InlineMarkerWrapper>
    )
  }

  if (marker.markerType === MARKER_TYPE_EXPLORE_HOTEL) {
    return (
      <InlineMarkerWrapper className={isHighlighted ? 'highlighted' : undefined}>
        <MarkerPin direction="horizontal" verticalAlign="center" horizontalAlign="center" className={MARKER_TYPE_EXPLORE_HOTEL}>
          <LineBedIcon size="XS" colour="neutral-eight" />
        </MarkerPin>
        {marker.label && <InlineMarkerLabel>{marker.label}</InlineMarkerLabel>}
      </InlineMarkerWrapper>
    )
  }

  if (marker.markerType === MARKER_TYPE_EXPLORE_EXPERIENCE) {
    return (
      <InlineMarkerWrapper className={isHighlighted ? 'highlighted' : undefined}>
        <MarkerPin direction="horizontal" verticalAlign="center" horizontalAlign="center" className={MARKER_TYPE_EXPLORE_EXPERIENCE}>
          <LineTicketIcon size="XS" colour="neutral-eight" />
        </MarkerPin>
        {marker.label && <InlineMarkerLabel>{marker.label}</InlineMarkerLabel>}
      </InlineMarkerWrapper>
    )
  }

  // Suggested places (location_entities) - show map marker icon
  return (
    <InlineMarkerWrapper>
      <MarkerPin direction="horizontal" verticalAlign="center" horizontalAlign="center" className={MARKER_TYPE_SUGGESTED}>
        <LineMapMarkerIcon size="XS" colour="neutral-eight" />
      </MarkerPin>
      {!deemphasizeBaseMarkers && marker.label && <InlineMarkerLabel>{marker.label}</InlineMarkerLabel>}
    </InlineMarkerWrapper>
  )
}

export default memo(ChatMapMarkerContent)
